import React, { useState, useEffect, useCallback, useRef } from "react";
import "@/App.css";
import axios from "axios";
import { 
  RefreshCw, 
  AlertTriangle, 
  CheckCircle, 
  Clock, 
  Users, 
  Calendar,
  ChevronDown,
  ChevronUp,
  FileText,
  Download,
  Loader2,
  LogOut,
  Lock,
  CalendarDays,
  Settings,
  Archive,
  ToggleLeft,
  ToggleRight,
  Shield,
  ShieldCheck
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Camp dates for 2026 season (June 29 - August 20, Mon-Fri)
const CAMP_START_DATE = new Date(2026, 5, 29); // June 29, 2026

// Generate camp day dates (Mon-Fri only)
const generateCampDates = () => {
  const dates = {};
  let currentDate = new Date(CAMP_START_DATE);
  let dayNum = 1;
  
  while (dayNum <= 38) {
    const dayOfWeek = currentDate.getDay();
    // Skip weekends (0 = Sunday, 6 = Saturday)
    if (dayOfWeek !== 0 && dayOfWeek !== 6) {
      dates[dayNum] = new Date(currentDate);
      dayNum++;
    }
    currentDate.setDate(currentDate.getDate() + 1);
  }
  return dates;
};

const CAMP_DATES = generateCampDates();

const formatCampDate = (dayNum) => {
  const date = CAMP_DATES[dayNum];
  if (!date) return "";
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
};

// Auth helper
const getToken = () => localStorage.getItem("auth_token");
const setToken = (token) => localStorage.setItem("auth_token", token);
const clearToken = () => localStorage.removeItem("auth_token");

const authHeaders = () => ({
  headers: { Authorization: `Bearer ${getToken()}` }
});

function LoginPage({ onLogin }) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const response = await axios.post(`${API}/auth/login`, { username, password });
      if (response.data.success) {
        setToken(response.data.token);
        onLogin();
      } else {
        setError(response.data.message || "Login failed");
      }
    } catch (e) {
      setError("Login failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-container" data-testid="login-page">
      <div className="login-card">
        <div className="login-header">
          <div className="login-logo">
            <img src="/logo.png" alt="Rolling River Day Camp" />
          </div>
          <h1>Rolling River Day Camp</h1>
          <p>Flex Enrollment Monitor</p>
        </div>

        <form onSubmit={handleSubmit} className="login-form">
          {error && (
            <div className="login-error" data-testid="login-error">
              <AlertTriangle size={16} />
              {error}
            </div>
          )}

          <div className="form-group">
            <label htmlFor="username">Username</label>
            <input
              id="username"
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="Enter username"
              required
              data-testid="username-input"
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">Password</label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter password"
              required
              data-testid="password-input"
            />
          </div>

          <button 
            type="submit" 
            className="login-button"
            disabled={loading}
            data-testid="login-button"
          >
            {loading ? (
              <>
                <Loader2 className="spin" size={18} />
                Signing in...
              </>
            ) : (
              <>
                <Lock size={18} />
                Sign In
              </>
            )}
          </button>
        </form>
      </div>
    </div>
  );
}

function CamperDetailCard({ camper }) {
  const selectedDays = camper.selected_days || [];
  const expectedDays = camper.days_expected;
  
  // Generate all days up to expected (for Flex 20, show days 1-20, etc.)
  const allDays = Array.from({ length: Math.max(expectedDays, 30) }, (_, i) => i + 1);
  
  return (
    <div className="camper-detail-card">
      <div className="camper-detail-header">
        <h4>
          <CalendarDays size={18} style={{ marginRight: 8, verticalAlign: 'middle' }} />
          {camper.first_name} {camper.last_name}'s Selected Days
        </h4>
        <span className={`status-badge ${camper.mismatch ? 'status-mismatch' : 'status-ok'}`}>
          {selectedDays.length} of {expectedDays} days selected
        </span>
      </div>
      <div className="days-grid">
        {allDays.slice(0, expectedDays).map((dayNum) => {
          const isSelected = selectedDays.includes(dayNum);
          return (
            <div 
              key={dayNum} 
              className={`day-chip ${isSelected ? 'selected' : 'not-selected'}`}
            >
              <span className="day-num">Day {dayNum}</span>
              <span className="day-date">{formatCampDate(dayNum)}</span>
              {isSelected && <CheckCircle size={14} />}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Dashboard({ onLogout }) {
  const [reports, setReports] = useState([]);
  const [latestReport, setLatestReport] = useState(null);
  const [selectedReport, setSelectedReport] = useState(null);
  const [isRunning, setIsRunning] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedCamper, setExpandedCamper] = useState(null);
  const [jobProgress, setJobProgress] = useState(null);
  const [schedulerStatus, setSchedulerStatus] = useState(null);
  const [approvedIds, setApprovedIds] = useState(new Set());
  const [showSettings, setShowSettings] = useState(false);
  const [archives, setArchives] = useState([]);
  const [archiving, setArchiving] = useState(false);
  const [archiveConfirm, setArchiveConfirm] = useState(false);
  const [showNotSubmitted, setShowNotSubmitted] = useState(false);
  const [notSubmittedCampers, setNotSubmittedCampers] = useState([]);
  const [notSubmittedLoading, setNotSubmittedLoading] = useState(false);
  const pollIntervalRef = useRef(null);

  const fetchReports = useCallback(async () => {
    try {
      const [reportsRes, latestRes, schedulerRes, approvedRes] = await Promise.all([
        axios.get(`${API}/reports`, authHeaders()),
        axios.get(`${API}/reports/latest/summary`, authHeaders()),
        axios.get(`${API}/scheduler/status`, authHeaders()).catch(() => null),
        axios.get(`${API}/campers/approved`, authHeaders()).catch(() => null)
      ]);
      setReports(reportsRes.data);
      setLatestReport(latestRes.data);
      if (schedulerRes) setSchedulerStatus(schedulerRes.data);
      if (approvedRes) setApprovedIds(new Set(approvedRes.data.approved || []));
      setError(null);
    } catch (e) {
      if (e.response?.status === 401) {
        onLogout();
      } else {
        console.error("Error fetching reports:", e);
        setError("Failed to load reports");
      }
    } finally {
      setLoading(false);
    }
  }, [onLogout]);

  useEffect(() => {
    fetchReports();
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, [fetchReports]);

  const pollJobStatus = async (jobId) => {
    try {
      const response = await axios.get(`${API}/jobs/${jobId}`, authHeaders());
      const job = response.data;
      
      setJobProgress({
        status: job.status,
        progress: job.progress,
        total: job.total,
        message: job.message
      });

      if (job.status === "completed") {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
        setIsRunning(false);
        setJobProgress(null);
        await fetchReports();
        if (job.report_id) {
          loadReportDetails(job.report_id);
        }
      } else if (job.status === "failed") {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
        setIsRunning(false);
        setJobProgress(null);
        setError(job.message || "Report generation failed");
      }
    } catch (e) {
      if (e.response?.status === 401) {
        onLogout();
      }
      console.error("Error polling job status:", e);
    }
  };

  const runReport = async () => {
    setIsRunning(true);
    setError(null);
    setJobProgress({ status: "starting", progress: 0, total: 0, message: "Starting report..." });
    
    try {
      const response = await axios.post(`${API}/reports/run`, {}, authHeaders());
      if (response.data.success && response.data.job_id) {
        pollIntervalRef.current = setInterval(() => {
          pollJobStatus(response.data.job_id);
        }, 1500);
      } else {
        setError(response.data.message || "Failed to start report");
        setIsRunning(false);
        setJobProgress(null);
      }
    } catch (e) {
      if (e.response?.status === 401) {
        onLogout();
      } else {
        console.error("Error running report:", e);
        setError("Failed to start report. Check CampMinder connection.");
      }
      setIsRunning(false);
      setJobProgress(null);
    }
  };

  const loadReportDetails = async (reportId) => {
    try {
      const response = await axios.get(`${API}/reports/${reportId}`, authHeaders());
      setSelectedReport(response.data);
      setExpandedCamper(null);
    } catch (e) {
      if (e.response?.status === 401) {
        onLogout();
      }
      console.error("Error loading report:", e);
    }
  };

  const downloadTextReport = async (reportId) => {
    try {
      const response = await axios.get(`${API}/reports/${reportId}/text`, authHeaders());
      const blob = new Blob([response.data], { type: "text/plain" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `flex-report-${reportId}.txt`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      if (e.response?.status === 401) {
        onLogout();
      }
      console.error("Error downloading report:", e);
    }
  };

  const toggleApproval = async (personId, e) => {
    e.stopPropagation();
    const pid = String(personId);
    const isApproved = approvedIds.has(pid);
    try {
      if (isApproved) {
        await axios.delete(`${API}/campers/${pid}/approve`, authHeaders());
        setApprovedIds(prev => { const n = new Set(prev); n.delete(pid); return n; });
      } else {
        await axios.post(`${API}/campers/${pid}/approve`, {}, authHeaders());
        setApprovedIds(prev => new Set(prev).add(pid));
      }
    } catch (err) {
      console.error("Error toggling approval:", err);
    }
  };

  const fetchArchives = async () => {
    try {
      const res = await axios.get(`${API}/settings/archives`, authHeaders());
      setArchives(res.data.archives || []);
    } catch (err) {
      console.error("Error fetching archives:", err);
    }
  };

  const archiveSeason = async () => {
    setArchiving(true);
    try {
      await axios.post(`${API}/settings/archive-season`, {}, authHeaders());
      setArchiveConfirm(false);
      await fetchArchives();
      await fetchReports();
    } catch (err) {
      console.error("Error archiving season:", err);
      setError("Failed to archive season");
    } finally {
      setArchiving(false);
    }
  };

  const fetchNotSubmitted = async () => {
    setNotSubmittedLoading(true);
    setShowNotSubmitted(true);
    try {
      const res = await axios.get(`${API}/reports/latest/not-submitted`, authHeaders());
      setNotSubmittedCampers(res.data.campers || []);
    } catch (err) {
      console.error("Error fetching not submitted:", err);
    } finally {
      setNotSubmittedLoading(false);
    }
  };

  const formatTimestamp = (ts) => {
    if (!ts) return "";
    const date = new Date(ts);
    return date.toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true
    });
  };

  const getStatusClass = (status) => {
    if (status.includes("OK")) return "status-ok";
    if (status.includes("UNDER") || status.includes("OVER")) return "status-mismatch";
    if (status.includes("ERROR")) return "status-error";
    return "status-pending";
  };

  const toggleCamperExpand = (idx) => {
    setExpandedCamper(expandedCamper === idx ? null : idx);
  };

  if (loading) {
    return (
      <div className="app-container" data-testid="loading-screen">
        <div className="loading-spinner">
          <Loader2 className="spin" size={48} />
          <p>Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="app-container" data-testid="dashboard">
      {/* Header */}
      <header className="app-header" data-testid="app-header">
        <div className="header-content">
          <div className="logo-section">
            <div className="logo-icon">
              <img src="/logo.png" alt="Rolling River" />
            </div>
            <div>
              <h1>Rolling River Day Camp</h1>
              <p className="subtitle">Flex Enrollment Monitor</p>
            </div>
          </div>
          <div className="header-actions">
            <button
              onClick={runReport}
              disabled={isRunning}
              className="run-button"
              data-testid="run-report-btn"
            >
              {isRunning ? (
                <>
                  <Loader2 className="spin" size={18} />
                  {jobProgress?.total > 0 
                    ? `${jobProgress.progress}/${jobProgress.total} campers...`
                    : jobProgress?.message || "Starting..."}
                </>
              ) : (
                <>
                  <RefreshCw size={18} />
                  Run Report Now
                </>
              )}
            </button>
            <button
              onClick={() => { setShowSettings(!showSettings); if (!showSettings) fetchArchives(); }}
              className={`settings-button ${showSettings ? 'active' : ''}`}
              data-testid="settings-btn"
            >
              <Settings size={18} />
            </button>
            <button
              onClick={onLogout}
              className="logout-button"
              data-testid="logout-btn"
            >
              <LogOut size={18} />
              Logout
            </button>
          </div>
        </div>
      </header>

      {/* Settings Panel */}
      {showSettings && (
        <div className="settings-panel" data-testid="settings-panel">
          <div className="settings-content">
            <h2><Settings size={20} /> Settings</h2>
            
            <div className="settings-section">
              <h3><Archive size={18} /> Season Archive</h3>
              <p className="settings-desc">
                Archive the current season's data (all reports, alerts, and approvals) and start fresh. 
                Archived data is preserved and can be viewed later. Use this at the end of each camp season.
              </p>
              
              {!archiveConfirm ? (
                <button 
                  className="archive-button"
                  onClick={() => setArchiveConfirm(true)}
                  data-testid="archive-season-btn"
                >
                  <Archive size={16} />
                  Archive Season 2026 & Start Fresh
                </button>
              ) : (
                <div className="archive-confirm" data-testid="archive-confirm">
                  <p className="confirm-warning">
                    <AlertTriangle size={16} />
                    This will archive all current reports, clear the mismatch board, and reset alerts. Are you sure?
                  </p>
                  <div className="confirm-actions">
                    <button 
                      className="confirm-yes"
                      onClick={archiveSeason}
                      disabled={archiving}
                      data-testid="archive-confirm-btn"
                    >
                      {archiving ? <><Loader2 className="spin" size={14} /> Archiving...</> : 'Yes, Archive & Start Fresh'}
                    </button>
                    <button 
                      className="confirm-no"
                      onClick={() => setArchiveConfirm(false)}
                      data-testid="archive-cancel-btn"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>

            {archives.length > 0 && (
              <div className="settings-section">
                <h3><Calendar size={18} /> Past Season Archives</h3>
                <div className="archives-list">
                  {archives.map((archive) => (
                    <div key={archive.id} className="archive-card" data-testid={`archive-${archive.id}`}>
                      <div className="archive-info">
                        <strong>Season {archive.season_id}</strong>
                        <span className="archive-date">
                          Archived {new Date(archive.archived_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                        </span>
                      </div>
                      <div className="archive-stats">
                        <span>{archive.report_count} reports</span>
                        <span>{archive.alert_count} alerts</span>
                        <span>{archive.approval_count} approvals</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      <main className="main-content">
        {/* Progress Banner */}
        {isRunning && jobProgress && (
          <div className="progress-banner" data-testid="progress-banner">
            <Loader2 className="spin" size={20} />
            <div className="progress-info">
              <span className="progress-message">{jobProgress.message}</span>
              {jobProgress.total > 0 && (
                <div className="progress-bar-container">
                  <div 
                    className="progress-bar-fill" 
                    style={{ width: `${(jobProgress.progress / jobProgress.total) * 100}%` }}
                  />
                </div>
              )}
            </div>
          </div>
        )}

        {error && (
          <div className="error-banner" data-testid="error-banner">
            <AlertTriangle size={20} />
            <span>{error}</span>
          </div>
        )}

        {/* Stats Cards */}
        {latestReport?.has_report && (
          <section className="stats-section" data-testid="stats-section">
            <h2 className="section-title">
              <Clock size={20} />
              Latest Report: {latestReport.report_date_display}
            </h2>
            <div className="stats-grid">
              <div className="stat-card" data-testid="stat-total">
                <div className="stat-icon total">
                  <Users size={24} />
                </div>
                <div className="stat-content">
                  <span className="stat-value">{latestReport.stats.total}</span>
                  <span className="stat-label">Total Campers</span>
                </div>
              </div>
              <div className="stat-card" data-testid="stat-submitted">
                <div className="stat-icon submitted">
                  <CheckCircle size={24} />
                </div>
                <div className="stat-content">
                  <span className="stat-value">{latestReport.stats.forms_submitted}</span>
                  <span className="stat-label">Forms Submitted</span>
                </div>
              </div>
              <div className="stat-card clickable" data-testid="stat-pending" onClick={fetchNotSubmitted}>
                <div className="stat-icon pending">
                  <Clock size={24} />
                </div>
                <div className="stat-content">
                  <span className="stat-value">{latestReport.stats.not_submitted}</span>
                  <span className="stat-label">Not Submitted</span>
                  <span className="stat-hint">Click to view</span>
                </div>
              </div>
              <div className={`stat-card ${(latestReport.stats.mismatches - approvedIds.size) > 0 ? 'highlight-alert' : ''}`} data-testid="stat-mismatches">
                <div className="stat-icon alert">
                  <AlertTriangle size={24} />
                </div>
                <div className="stat-content">
                  <span className="stat-value">{latestReport.stats.mismatches - approvedIds.size}</span>
                  <span className="stat-label">Mismatches</span>
                </div>
              </div>
            </div>

            {/* Program breakdown */}
            <div className="program-breakdown" data-testid="program-breakdown">
              <div className="program-item">
                <span className="program-badge flex20">Flex 20</span>
                <span className="program-count">{latestReport.stats.flex_20}</span>
              </div>
              <div className="program-item">
                <span className="program-badge flex25">Flex 25</span>
                <span className="program-count">{latestReport.stats.flex_25}</span>
              </div>
              <div className="program-item">
                <span className="program-badge flex30">Flex 30</span>
                <span className="program-count">{latestReport.stats.flex_30}</span>
              </div>
            </div>

            {/* Mismatch alerts */}
            {latestReport.mismatched_campers?.length > 0 && (
              <div className="mismatch-alerts" data-testid="mismatch-alerts">
                <h3>
                  <AlertTriangle size={18} />
                  Mismatches ({latestReport.mismatched_campers.filter(m => !approvedIds.has(String(m.person_id))).length})
                  {approvedIds.size > 0 && (
                    <span className="approved-count">{approvedIds.size} verified</span>
                  )}
                </h3>
                <ul>
                  {latestReport.mismatched_campers
                    .filter(m => !approvedIds.has(String(m.person_id)))
                    .map((m, idx) => (
                    <li key={idx} className={`alert-item ${m.mismatch_type.toLowerCase()}`}>
                      <span className="camper-name">{m.last_name}, {m.first_name}</span>
                      <span className="program-badge small">{m.program}</span>
                      <span className={`mismatch-badge ${m.mismatch_type.toLowerCase()}`}>
                        {m.mismatch_type}: {m.days_selected} of {m.days_expected} days
                      </span>
                      <button 
                        className="approve-toggle"
                        onClick={(e) => toggleApproval(m.person_id, e)}
                        title="Mark as verified OK"
                        data-testid={`approve-btn-${m.person_id}`}
                      >
                        <Shield size={16} />
                        Verify OK
                      </button>
                    </li>
                  ))}
                </ul>
                {/* Show approved/verified campers in collapsed section */}
                {latestReport.mismatched_campers.filter(m => approvedIds.has(String(m.person_id))).length > 0 && (
                  <div className="approved-section" data-testid="approved-section">
                    <h4 className="approved-header">
                      <ShieldCheck size={16} />
                      Verified OK ({latestReport.mismatched_campers.filter(m => approvedIds.has(String(m.person_id))).length})
                    </h4>
                    <ul>
                      {latestReport.mismatched_campers
                        .filter(m => approvedIds.has(String(m.person_id)))
                        .map((m, idx) => (
                        <li key={idx} className="alert-item approved">
                          <span className="camper-name">{m.last_name}, {m.first_name}</span>
                          <span className="program-badge small">{m.program}</span>
                          <span className={`mismatch-badge ${m.mismatch_type.toLowerCase()}`}>
                            {m.mismatch_type}: {m.days_selected} of {m.days_expected} days
                          </span>
                          <button 
                            className="approve-toggle active"
                            onClick={(e) => toggleApproval(m.person_id, e)}
                            title="Remove verification"
                            data-testid={`unapprove-btn-${m.person_id}`}
                          >
                            <ShieldCheck size={16} />
                            Verified
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </section>
        )}

        {/* Report History */}
        <section className="reports-section" data-testid="reports-section">
          <h2 className="section-title">
            <Calendar size={20} />
            Report History
          </h2>
          
          {reports.length === 0 ? (
            <div className="empty-state" data-testid="empty-state">
              <FileText size={48} />
              <p>No reports generated yet</p>
              <button onClick={runReport} className="run-button" data-testid="run-first-report-btn">
                Run First Report
              </button>
            </div>
          ) : (
            <div className="reports-list" data-testid="reports-list">
              {reports.map((report) => (
                <div
                  key={report.id}
                  className={`report-card ${selectedReport?.id === report.id ? 'selected' : ''}`}
                  data-testid={`report-card-${report.id}`}
                >
                  <div 
                    className="report-header"
                    onClick={() => loadReportDetails(report.id)}
                  >
                    <div className="report-info">
                      <span className="report-date">{report.report_date_display}</span>
                      <span className="report-time">{formatTimestamp(report.timestamp)}</span>
                    </div>
                    <div className="report-stats">
                      <span className="camper-count">{report.total_campers} campers</span>
                      {report.mismatches > 0 ? (
                        <span className="mismatch-count alert">
                          <AlertTriangle size={14} />
                          {report.mismatches} mismatches
                        </span>
                      ) : (
                        <span className="mismatch-count ok">
                          <CheckCircle size={14} />
                          No mismatches
                        </span>
                      )}
                    </div>
                    <button
                      className="icon-button"
                      onClick={(e) => {
                        e.stopPropagation();
                        downloadTextReport(report.id);
                      }}
                      title="Download text report"
                      data-testid={`download-btn-${report.id}`}
                    >
                      <Download size={18} />
                    </button>
                    {selectedReport?.id === report.id ? (
                      <ChevronUp size={20} />
                    ) : (
                      <ChevronDown size={20} />
                    )}
                  </div>
                  
                  {/* Expanded report details */}
                  {selectedReport?.id === report.id && (
                    <div className="report-details" data-testid="report-details">
                      <table className="campers-table">
                        <thead>
                          <tr>
                            <th>Last Name</th>
                            <th>First Name</th>
                            <th>Program</th>
                            <th>Form</th>
                            <th>Days Selected</th>
                            <th>Days Expected</th>
                            <th>Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {selectedReport.campers.map((camper, idx) => (
                            <React.Fragment key={camper.person_id || idx}>
                              <tr 
                                className={`${camper.mismatch ? 'mismatch-row' : ''} ${expandedCamper === idx ? 'expanded' : ''}`}
                                onClick={() => toggleCamperExpand(idx)}
                                data-testid={`camper-row-${idx}`}
                              >
                                <td>{camper.last_name}</td>
                                <td>{camper.first_name}</td>
                                <td>
                                  <span className={`program-badge small ${camper.program.toLowerCase().replace(' ', '')}`}>
                                    {camper.program}
                                  </span>
                                </td>
                                <td>{camper.form_submitted}</td>
                                <td>{camper.days_selected}</td>
                                <td>{camper.days_expected}</td>
                                <td>
                                  <span className={`status-badge ${getStatusClass(camper.status)}`}>
                                    {camper.status}
                                  </span>
                                </td>
                              </tr>
                              {expandedCamper === idx && (
                                <tr key={`${idx}-detail`} className="camper-detail-row">
                                  <td colSpan="7">
                                    <CamperDetailCard camper={camper} />
                                  </td>
                                </tr>
                              )}
                            </React.Fragment>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Schedule Info */}
        <section className="schedule-info" data-testid="schedule-info">
          <div className="schedule-card">
            <Clock size={20} />
            <div>
              <strong>Camp Season 2026</strong>
              <p>June 29 - August 20 (Mon-Fri)</p>
            </div>
          </div>
          <div className={`schedule-card ${schedulerStatus?.enabled ? 'schedule-active' : ''}`}>
            <RefreshCw size={20} />
            <div>
              <strong>Daily Auto-Report</strong>
              {schedulerStatus ? (
                <>
                  <p>{schedulerStatus.schedule} {schedulerStatus.enabled ? '(Active)' : '(Inactive)'}</p>
                  {schedulerStatus.next_run_display && (
                    <p className="schedule-next">Next run: {schedulerStatus.next_run_display}</p>
                  )}
                  {schedulerStatus.last_scheduled_result && (
                    <p className="schedule-last">Last result: {schedulerStatus.last_scheduled_result}</p>
                  )}
                </>
              ) : (
                <p>Loading scheduler status...</p>
              )}
            </div>
          </div>
          <div className="schedule-card">
            <AlertTriangle size={20} />
            <div>
              <strong>Mismatch Alerts</strong>
              <p>Sent to colleen@rollingriver.com & marissa@rollingriver.com</p>
            </div>
          </div>
        </section>
      </main>

      <footer className="app-footer">
        <p>Rolling River Day Camp © 2026 | Flex Enrollment Monitor | Made by Brian Stein</p>
      </footer>

      {/* Not Submitted Modal */}
      {showNotSubmitted && (
        <div className="modal-overlay" onClick={() => setShowNotSubmitted(false)} data-testid="not-submitted-modal">
          <div className="modal-content not-submitted-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2><Clock size={20} /> Missing Flex Forms ({notSubmittedCampers.length})</h2>
              <button className="modal-close" onClick={() => setShowNotSubmitted(false)} data-testid="close-not-submitted">×</button>
            </div>
            {notSubmittedLoading ? (
              <div className="modal-loading"><Loader2 className="spin" size={24} /> Loading...</div>
            ) : (
              <>
                <div className="not-submitted-list" data-testid="not-submitted-list">
                  <table className="not-submitted-table">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Camper</th>
                        <th>Program</th>
                        <th>Parent / Guardian</th>
                      </tr>
                    </thead>
                    <tbody>
                      {notSubmittedCampers.map((c, idx) => (
                        <tr key={c.person_id || idx} data-testid={`not-submitted-row-${idx}`}>
                          <td className="row-num">{idx + 1}</td>
                          <td>
                            <div className="camper-last">{c.last_name}, {c.first_name}</div>
                          </td>
                          <td><span className={`program-badge small ${c.program.toLowerCase().replace(' ', '')}`}>{c.program}</span></td>
                          <td>
                            {c.parent_name ? (
                              <div className="parent-info">
                                <div className="parent-name">{c.parent_name}</div>
                                <div className="parent-contact">
                                  {c.parent_phone && <span className="parent-phone">{c.parent_phone}</span>}
                                  {c.parent_email && <span className="parent-email"><a href={`mailto:${c.parent_email}`}>{c.parent_email}</a></span>}
                                </div>
                              </div>
                            ) : (
                              <span className="no-parent-info">—</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [checkingAuth, setCheckingAuth] = useState(true);

  useEffect(() => {
    // Check if user has valid token
    const token = getToken();
    if (token) {
      axios.get(`${API}/auth/verify`, authHeaders())
        .then(() => setIsAuthenticated(true))
        .catch(() => {
          clearToken();
          setIsAuthenticated(false);
        })
        .finally(() => setCheckingAuth(false));
    } else {
      setCheckingAuth(false);
    }
  }, []);

  const handleLogin = () => {
    setIsAuthenticated(true);
  };

  const handleLogout = () => {
    clearToken();
    setIsAuthenticated(false);
  };

  if (checkingAuth) {
    return (
      <div className="app-container">
        <div className="loading-spinner">
          <Loader2 className="spin" size={48} />
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <LoginPage onLogin={handleLogin} />;
  }

  return <Dashboard onLogout={handleLogout} />;
}

export default App;
